import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  HardDrive, Database, Layers
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const DVRsPage = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('config', 'dvrs');
  const [dvrsList, setDvrsList] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_channels: 0, total_storage_tb: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', site: '', brand: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const pageSize = 10;

  // Form options
  const [options, setOptions] = useState({ 
    marcas: [], sites: [], ubicaciones: [], estados: [], proveedores: [] 
  });

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, dvr: null });
  const [editModal, setEditModal] = useState({ open: false, dvr: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, dvr: null });
  const [formData, setFormData] = useState({
    name: '', model: '', ip_address: '', mac_address: '', brand_id: '',
    site_id: '', location_id: '', channels: '', storage_tb: '',
    http_port: '80', rtsp_port: '554', provider_id: '', status_id: '',
    installation_date: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/config/dvrs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/config/dvrs/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchDvrsList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.site) params.append('site', filters.site);
      if (filters.brand) params.append('brand', filters.brand);

      const response = await api.get(`/config/dvrs?${params}`);
      setDvrsList(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar DVRs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
  }, []);

  useEffect(() => {
    fetchDvrsList();
  }, [page, filters.search, filters.site, filters.brand, sortConfig]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Marca badge
  const getMarcaBadge = (marca) => {
    if (!marca) return <span className="badge badge-outline">Sin marca</span>;
    const m = marca.toLowerCase();
    if (m.includes('dahua')) return <span className="badge badge-success">Dahua</span>;
    if (m.includes('hikvision')) return <span className="badge badge-info">Hikvision</span>;
    if (m.includes('uniview')) return <span className="badge badge-warning">Uniview</span>;
    return <span className="badge badge-outline">{marca}</span>;
  };

  // View modal
  const handleView = (dvr) => {
    setViewModal({ open: true, dvr });
  };

  // Edit modal
  const handleEdit = (dvr) => {
    setFormData({
      name: dvr.name || '',
      model: dvr.model || '',
      ip_address: dvr.ip_address || '',
      mac_address: dvr.mac_address || '',
      brand_id: dvr.brand_id?.toString() || '',
      site_id: dvr.site_id?.toString() || '',
      location_id: dvr.location_id?.toString() || '',
      channels: dvr.channels?.toString() || '',
      storage_tb: dvr.storage_tb?.toString() || '',
      http_port: dvr.http_port?.toString() || '80',
      rtsp_port: dvr.rtsp_port?.toString() || '554',
      provider_id: dvr.provider_id?.toString() || '',
      status_id: dvr.status_id?.toString() || '',
      installation_date: dvr.installation_date || '',
      notes: dvr.notes || ''
    });
    setEditModal({ open: true, dvr });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      name: '', model: '', ip_address: '', mac_address: '', brand_id: '',
      site_id: '', location_id: '', channels: '', storage_tb: '',
      http_port: '80', rtsp_port: '554', provider_id: '', status_id: '',
      installation_date: '', notes: ''
    });
    setEditModal({ open: true, dvr: null });
  };

  // Save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        channels: formData.channels ? parseInt(formData.channels) : null,
        storage_tb: formData.storage_tb ? parseFloat(formData.storage_tb) : null,
        http_port: formData.http_port ? parseInt(formData.http_port) : 80,
        rtsp_port: formData.rtsp_port ? parseInt(formData.rtsp_port) : 554,
        provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        installation_date: formData.installation_date || null
      };

      if (editModal.dvr) {
        await api.put(`/config/dvrs/${editModal.dvr.id}`, payload);
        toast.success('DVR actualizado exitosamente');
      } else {
        await api.post('/config/dvrs', payload);
        toast.success('DVR creado exitosamente');
      }
      setEditModal({ open: false, dvr: null });
      fetchDvrsList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/config/dvrs/${deleteModal.dvr.id}`);
      toast.success('DVR eliminado exitosamente');
      setDeleteModal({ open: false, dvr: null });
      fetchDvrsList();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Get unique marcas for filter
  const uniqueMarcas = [...new Set(options.marcas.map(m => m.nombre).filter(Boolean))];

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver DVRs</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">DVR / NVR</h1>
            <p className="page-subtitle">Grabadores de video para camaras de seguridad</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-dvr-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo DVR
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="dvrs-stats">
          <div className="stats-card" data-testid="stats-total">
            <div className="mb-2">
              <HardDrive className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total DVRs</div>
          </div>
          <div className="stats-card" data-testid="stats-channels">
            <div className="mb-2">
              <Layers className="h-8 w-8 text-blue-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-blue-500">{stats.total_channels}</div>
            <div className="stats-card-label">Canales</div>
          </div>
          <div className="stats-card" data-testid="stats-storage">
            <div className="mb-2">
              <Database className="h-8 w-8 text-green-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-green-500">{stats.total_storage_tb} TB</div>
            <div className="stats-card-label">Almacenamiento</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_address')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip_address" />
                          </div>
                        </th>
                        <th className="text-center">MARCA</th>
                        <th>MODELO</th>
                        <th 
                          className="text-center cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('channels')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            CANALES
                            <SortIcon field="channels" />
                          </div>
                        </th>
                        <th 
                          className="text-center cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('storage_tb')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            STORAGE
                            <SortIcon field="storage_tb" />
                          </div>
                        </th>
                        <th>SITE</th>
                        <th className="w-24 text-center">ACCIONES</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.brand || "todos"}
                            onValueChange={(v) => handleFilterChange('brand', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {uniqueMarcas.map(marca => (
                                <SelectItem key={marca} value={marca}>{marca}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.site || "todos"}
                            onValueChange={(v) => handleFilterChange('site', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {options.sites.map(site => (
                                <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {dvrsList.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <HardDrive className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron DVRs</p>
                            </div>
                          </td>
                        </tr>
                      ) : dvrsList.map((dvr) => (
                        <tr key={dvr.id} data-testid={`dvr-row-${dvr.id}`}>
                          <td className="font-medium text-primary">{dvr.name}</td>
                          <td className="font-mono text-sm text-muted-foreground">{dvr.ip_address || '-'}</td>
                          <td className="text-center">{getMarcaBadge(dvr.marca_nombre)}</td>
                          <td className="text-muted-foreground">{dvr.model || '-'}</td>
                          <td className="text-center font-medium">{dvr.channels || '-'}</td>
                          <td className="text-center text-muted-foreground">{dvr.storage_tb ? `${dvr.storage_tb} TB` : '-'}</td>
                          <td className="text-muted-foreground">{dvr.site_nombre || '-'}</td>
                          <td className="w-24">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(dvr)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-dvr-${dvr.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(dvr)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-dvr-${dvr.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, dvr })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-dvr-${dvr.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, dvr: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-primary" strokeWidth={1.5} />
                {viewModal.dvr?.name}
              </DialogTitle>
            </DialogHeader>
            {viewModal.dvr && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <p className="font-medium">{viewModal.dvr.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP</Label>
                    <p className="font-mono">{viewModal.dvr.ip_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">MAC</Label>
                    <p className="font-mono text-sm">{viewModal.dvr.mac_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Marca</Label>
                    <div className="mt-1">{getMarcaBadge(viewModal.dvr.marca_nombre)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <p className="text-sm">{viewModal.dvr.model || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Canales</Label>
                    <p className="text-sm font-medium">{viewModal.dvr.channels || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Almacenamiento</Label>
                    <p className="text-sm">{viewModal.dvr.storage_tb ? `${viewModal.dvr.storage_tb} TB` : '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.dvr.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.dvr.ubicacion_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Estado</Label>
                    <p>{viewModal.dvr.status_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.dvr.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Puertos</Label>
                    <p className="text-sm font-mono">HTTP: {viewModal.dvr.http_port} | RTSP: {viewModal.dvr.rtsp_port}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fecha Instalacion</Label>
                    <p>{viewModal.dvr.installation_date || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Notas</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.dvr.notes || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, dvr: null })}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-primary" strokeWidth={1.5} />
                {editModal.dvr ? 'Editar DVR' : 'Nuevo DVR'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre del DVR"
                  data-testid="input-name"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">IP</Label>
                <Input
                  value={formData.ip_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="192.168.1.100"
                  data-testid="input-ip"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">MAC</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  data-testid="input-mac"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Marca</Label>
                <Select
                  value={formData.brand_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-marca">
                    <SelectValue placeholder="Seleccionar marca" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin marca</SelectItem>
                    {options.marcas.map(marca => (
                      <SelectItem key={marca.id} value={marca.id.toString()}>{marca.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Modelo del DVR"
                  data-testid="input-model"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Canales</Label>
                <Input
                  type="number"
                  value={formData.channels}
                  onChange={(e) => setFormData(prev => ({ ...prev, channels: e.target.value }))}
                  placeholder="16"
                  data-testid="input-channels"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Almacenamiento (TB)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.storage_tb}
                  onChange={(e) => setFormData(prev => ({ ...prev, storage_tb: e.target.value }))}
                  placeholder="4.0"
                  data-testid="input-storage"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Site</Label>
                <Select
                  value={formData.site_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-site">
                    <SelectValue placeholder="Seleccionar site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin site</SelectItem>
                    {options.sites.map(site => (
                      <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ubicacion</Label>
                <Select
                  value={formData.location_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-ubicacion">
                    <SelectValue placeholder="Seleccionar ubicacion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin ubicacion</SelectItem>
                    {options.ubicaciones.map(ub => (
                      <SelectItem key={ub.id} value={ub.id.toString()}>{ub.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-estado">
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin estado</SelectItem>
                    {options.estados.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <Select
                  value={formData.provider_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, provider_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-proveedor">
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {options.proveedores.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Puerto HTTP</Label>
                <Input
                  type="number"
                  value={formData.http_port}
                  onChange={(e) => setFormData(prev => ({ ...prev, http_port: e.target.value }))}
                  placeholder="80"
                  data-testid="input-http"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Puerto RTSP</Label>
                <Input
                  type="number"
                  value={formData.rtsp_port}
                  onChange={(e) => setFormData(prev => ({ ...prev, rtsp_port: e.target.value }))}
                  placeholder="554"
                  data-testid="input-rtsp"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fecha Instalacion</Label>
                <Input
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, installation_date: e.target.value }))}
                  data-testid="input-fecha"
                />
              </div>
              
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                  data-testid="input-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, dvr: null })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, dvr: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar DVR
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el DVR <strong>{deleteModal.dvr?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, dvr: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DVRsPage;
